"use client";

/**
 * "Same property elsewhere" - cross-source duplicate listings.
 *
 * Posts the analysed listing's structural fingerprint (coords + area +
 * rooms + floor + mode) to /api/listings/duplicates and renders the other
 * active listings that look like the same physical unit.
 *
 * Because every match IS the same unit, we don't show conflicting
 * per-source AI verdicts (those diverge only because each listing was
 * batch-scored at a different time). Instead we show ONE reconciled fair
 * value for the unit plus the asking-price spread across sources, and a
 * per-match badge: green when the source agrees on every attribute, yellow
 * naming the genuine mismatch (deed / renovation missing or different, or a
 * materially distant pin) otherwise. A different area *label* is never a
 * mismatch - each site names the area its own way.
 *
 * Renders nothing while loading or when there are no matches, so it adds no
 * empty chrome to the result page. Used by ResultCard, so it covers the
 * fresh-analyse, history-detail, and shared-result surfaces uniformly.
 */

import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { Building2, Lock, ChevronRight } from "lucide-react";
import { API_BASE, formatPrice } from "@/lib/api";
import { canonicalListingKey } from "@/lib/listing-url";
import { useT } from "@/lib/locale-context";
import { useAuth } from "@/lib/auth-context";
import { hasProFeatures } from "@/lib/pro";
import { PRO_BADGE } from "@/lib/ui-classes";
import Link from "@/lib/localized-link";
import ProLine from "@/components/result/ProLine";
import Photo from "@/components/Photo";
import { withDetailBody } from "@/components/listings/detail-body-loader";

// mapbox-gl (inside the panel's mini map) touches `window` at module
// load; fetch the chunk only once a row is actually clicked (with the
// drawer's content chunk alongside).
const ListingDetailPanel = dynamic(
  () => withDetailBody(import("@/components/listings/ListingDetailPanel")),
  { ssr: false },
);

interface DuplicateItem {
  source?: string | null;
  url: string;
  asking_price?: number | null;
  area_m2?: number | null;
  room_count?: number | null;
  floor?: number | null;
  total_floors?: number | null;
  location_name?: string | null;
  complex_name?: string | null;
  has_repair?: boolean | null;
  has_bill_of_sale?: boolean | null;
  verdict_tier?: string | null;
  overpriced_pct?: number | null;
  thumbnail_url?: string | null;
  /** Metres between the pins; null when the analysed pin was a place
   *  point and the twin was matched by residence. */
  distance_m: number | null;
  /** The backend's perceptual photo hashes also matched - the strongest
   *  "same unit" confirmation. */
  photos_match?: boolean;
}

/** An active listing reusing this listing's photos WITHOUT matching it
 *  structurally - agent re-posts of the same flat, or lifted photos. */
interface PhotoMatchItem {
  source?: string | null;
  url: string;
  asking_price?: number | null;
  area_m2?: number | null;
  room_count?: number | null;
  floor?: number | null;
  total_floors?: number | null;
  location_name?: string | null;
  complex_name?: string | null;
  thumbnail_url?: string | null;
  shared_photos: number;
  /** Both sellers known and DIFFERENT - photo theft / bait signal. */
  different_seller?: boolean;
}

interface Props {
  source?: string | null;
  url?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  price?: number | null;          // analysed listing's asking price (for the spread)
  area_m2?: number | null;
  room_count?: number | null;
  floor?: number | null;
  total_floors?: number | null;
  has_repair?: boolean | null;
  has_bill_of_sale?: boolean | null;
  mode?: "sale" | "rent";
  /** The analysed listing's photo URLs - lets the backend hash them
   *  against its duplicate-photo index when the listing isn't indexed. */
  photoUrls?: string[] | null;
  /** Matched residence (catalog cluster id): its rows count as the same
   *  building even when the pins sit apart. */
  complexSlug?: string | null;
  /** The pin is a place point, not the building (tap.az, lalafo.az):
   *  twins come by residence only and the distance is not reported. */
  coordsApprox?: boolean;
  /** How a locked teaser reads. "card" (the listing page): the count and
   *  the Pro line with its link. "line" (the result's "Ətraflı təhlil",
   *  owner decision 5, 2026-10-29): one locked line without a link of its
   *  own, the result's one offer being its Pro line; never for an account
   *  that has Pro. */
  lockedAs?: "card" | "line";
}

// Beyond this the pin is far enough to flag (the finder already caps
// candidates at 150 m, so anything under this reads as the same building
// pinned slightly differently and stays green).
const LOC_DIFF_M = 70;

export default function DuplicateListings({
  source,
  url,
  latitude,
  longitude,
  price,
  area_m2,
  room_count,
  floor,
  total_floors,
  has_repair,
  has_bill_of_sale,
  mode = "sale",
  photoUrls,
  complexSlug,
  coordsApprox = false,
  lockedAs = "card",
}: Props) {
  const t = useT();
  const { user } = useAuth();
  const [items, setItems] = useState<DuplicateItem[] | null>(null);
  const [photoMatches, setPhotoMatches] = useState<PhotoMatchItem[]>([]);
  const [photoMatchTotal, setPhotoMatchTotal] = useState(0);
  // PRO GATE: the server strips the match lists for non-Pro viewers
  // and ships only how many it found - the honest teaser.
  const [lockedTotal, setLockedTotal] = useState(0);
  const [photoQueryTotal, setPhotoQueryTotal] = useState(0);
  // Different-seller photo reuse count - ships even in the locked
  // response (a fraud warning is safety information, not a perk).
  const [foreignReuse, setForeignReuse] = useState(0);
  // Clicked row -> live listing drawer (same panel as /listings and
  // /map) instead of bouncing straight out to the source site. The
  // canonical URL key ("bina.az:12345") supplies the ids the panel
  // needs; rows whose URL we can't canonicalize keep the old
  // open-in-new-tab behavior.
  const [active, setActive] = useState<{
    externalId: string;
    listingId: string;
    sourceUrl: string;
  } | null>(null);
  const openRow = (rowUrl: string) => {
    const key = canonicalListingKey(rowUrl);
    if (!key) {
      window.open(rowUrl, "_blank", "noopener,noreferrer");
      return;
    }
    const [src, ext] = key.split(":");
    setActive({
      externalId: ext,
      listingId: `${src.replace(/\./g, "_")}:${ext}`,
      sourceUrl: rowUrl,
    });
  };

  useEffect(() => {
    // The structural fingerprint needs coords + area + rooms, but the
    // photo-hash lookup works from photos alone - only bail when
    // NEITHER signal is available.
    const hasFingerprint =
      latitude != null && longitude != null && area_m2 != null && room_count != null;
    const hasPhotos = !!photoUrls?.length;
    if (!hasFingerprint && !hasPhotos) {
      setItems([]);
      return;
    }
    // Derive the source listing id from its URL so a bina.az listing can
    // exclude itself. canonicalListingKey returns "<source>:<id>".
    let listingId: string | null = null;
    if (url) {
      const key = canonicalListingKey(url);
      if (key) listingId = key.slice(key.indexOf(":") + 1);
    }

    const ctrl = new AbortController();
    fetch(`${API_BASE}/listings/duplicates`, {
      method: "POST",
      // The endpoint resolves the viewer's FEATURE tier now (Pro gate);
      // without the session cookie a Pro/trial user reads as anonymous
      // and gets the locked teaser.
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      signal: ctrl.signal,
      body: JSON.stringify({
        source: source ?? null,
        listing_id: listingId,
        latitude,
        longitude,
        area_m2,
        room_count,
        floor: floor ?? null,
        total_floors: total_floors ?? null,
        has_bill_of_sale: has_bill_of_sale ?? null,
        mode,
        photo_urls: photoUrls?.slice(0, 12) ?? null,
        complex_slug: complexSlug ?? null,
        coords_approx: coordsApprox,
      }),
    })
      .then((r) => (r.ok ? r.json() : { duplicates: [] }))
      .then((d) => {
        if (ctrl.signal.aborted) return;
        setItems(d.duplicates || []);
        setPhotoMatches(d.photo_matches || []);
        setPhotoMatchTotal(d.photo_match_total || 0);
        setPhotoQueryTotal(d.photo_query_total || 0);
        setLockedTotal(d.locked ? (d.locked_total || 0) : 0);
        setForeignReuse(d.foreign_reuse || 0);
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setItems([]);
      });
    return () => ctrl.abort();
  }, [source, url, latitude, longitude, area_m2, room_count, floor, total_floors, has_bill_of_sale, mode, photoUrls, complexSlug, coordsApprox]);

  // Locked teaser: matches exist, the viewer's tier can't see them.
  // Real count, zero details - and nothing renders when nothing was
  // found (no invented scarcity). The way in is one quiet sentence with a
  // link, the result's Pro line, not a full-width button (owner,
  // 2026-10-22).
  if (lockedTotal > 0 && lockedAs === "line") {
    if (hasProFeatures(user)) return null;
    return (
      <section className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5 sm:px-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-bold text-gray-900">{t("result.duplicates.title")}</h3>
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${PRO_BADGE}`}>
            <Lock className="w-3 h-3" aria-hidden /> Pro
          </span>
        </div>
        {/* The fraud warning is never paywalled - only the details are. */}
        {foreignReuse > 0 && (
          <p className="mt-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 leading-relaxed">
            {t("result.duplicates.reuse_warn")}
          </p>
        )}
        <p className="mt-1 text-[13px] leading-relaxed text-gray-600">
          {t("result.duplicates.locked").replace("{n}", String(lockedTotal))}
        </p>
      </section>
    );
  }
  if (lockedTotal > 0) {
    return (
      <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs font-semibold text-gray-500">
            {t("result.duplicates.title")}
          </div>
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${PRO_BADGE}`}
          >
            <Lock className="w-3 h-3" /> Pro
          </span>
        </div>
        {/* The fraud warning is never paywalled - only the details are. */}
        {foreignReuse > 0 && (
          <p className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 leading-relaxed">
            {t("result.duplicates.reuse_warn")}
          </p>
        )}
        <ProLine
          className="mt-2"
          what={t("result.duplicates.locked").replace("{n}", String(lockedTotal))}
        />
      </section>
    );
  }

  if (!items || (items.length === 0 && photoMatches.length === 0)) return null;

  const priceUnit = mode === "rent" ? t("result.permonth") : "AZN";

  // Asking-price spread across every source (the analysed listing + matches).
  const prices = [price, ...items.map((d) => d.asking_price)].filter(
    (p): p is number => p != null && p > 0,
  );
  const minPrice = prices.length ? Math.min(...prices) : null;
  const maxPrice = prices.length ? Math.max(...prices) : null;
  const hasSpread = minPrice != null && maxPrice != null && maxPrice > minPrice;

  // The genuine attribute mismatches for one match vs the analysed listing.
  // Deliberately NOT including the location label (sites differ) or price
  // (that's the spread, shown above).
  function diffsFor(d: DuplicateItem): string[] {
    const out: string[] = [];
    if (has_bill_of_sale != null) {
      if (d.has_bill_of_sale == null) out.push(t("result.duplicates.diff.deed_unknown"));
      else if (d.has_bill_of_sale !== has_bill_of_sale) out.push(t("result.duplicates.diff.deed"));
    }
    if (has_repair != null) {
      if (d.has_repair == null) out.push(t("result.duplicates.diff.repair_unknown"));
      else if (d.has_repair !== has_repair) out.push(t("result.duplicates.diff.repair"));
    }
    if (d.distance_m != null && d.distance_m > LOC_DIFF_M) {
      out.push(t("result.duplicates.diff.location").replace("{m}", String(Math.round(d.distance_m))));
    }
    return out;
  }

  // One merged, ranked list - strongest same-unit evidence first:
  //   1. structural match with photo confirmation (fewest diffs first)
  //   2. structural match only (fewest diffs first)
  //   3. photo-only re-posts (most shared photos first)
  type MergedRow =
    | { kind: "structural"; d: DuplicateItem }
    | { kind: "photo"; m: PhotoMatchItem };
  const merged: MergedRow[] = [
    ...[...items]
      .sort((a, b) => {
        const pa = a.photos_match ? 0 : 1;
        const pb = b.photos_match ? 0 : 1;
        if (pa !== pb) return pa - pb;
        return diffsFor(a).length - diffsFor(b).length;
      })
      .map((d) => ({ kind: "structural" as const, d })),
    ...[...photoMatches]
      .sort((a, b) => b.shared_photos - a.shared_photos)
      .map((m) => ({ kind: "photo" as const, m })),
  ];

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-sm font-bold text-gray-900">
          {t("result.duplicates.title")}
        </h3>
        <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-gray-100 text-gray-700 text-xs font-bold tabular-nums">
          {merged.length}
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-1 leading-relaxed">
        {t("result.duplicates.subtitle")}
      </p>
      {/* Photo-reuse caution, folded into the same section: the tail of
          the list is photo-only re-posts describing different facts. */}
      {photoMatchTotal > 0 && (
        <p className="text-xs text-amber-700 mb-3 leading-relaxed">
          {t("result.photodups.warning").replace(
            "{n}",
            String(Math.max(photoMatchTotal, photoMatches.length)),
          )}
        </p>
      )}
      {/* Different-seller reuse: someone ELSE is posting these photos -
          photo theft or a bait listing. Strongest trust signal in the
          section, so it gets the rose banner, not an amber footnote. */}
      {foreignReuse > 0 && (
        <p className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 leading-relaxed">
          {t("result.duplicates.reuse_warn")}
        </p>
      )}

      {/* Asking-price spread across the structural matches, so the same
          flat never shows conflicting per-source verdicts. */}
      {items.length > 0 && hasSpread && (
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-gray-200 px-3 py-2 text-xs">
          <span className="text-gray-600">
            {t("result.duplicates.pricerange")}:{" "}
            <span className="font-bold text-gray-900 tabular-nums">
              {formatPrice(minPrice!)}-{formatPrice(maxPrice!)} <span className="font-medium text-gray-400">{priceUnit}</span>
            </span>
          </span>
        </div>
      )}

      {/* ~5 rows visible; longer lists scroll inside the card so the
          section can't dominate the page (real clusters hit 7-8). */}
      <div className={`space-y-2 ${merged.length > 5 ? "max-h-[400px] overflow-y-auto pr-1" : ""}`}>
        {merged.map((row) => {
          const it = row.kind === "structural" ? row.d : row.m;
          const diffs = row.kind === "structural" ? diffsFor(row.d) : [];
          const isCheapest =
            row.kind === "structural" && hasSpread && row.d.asking_price === minPrice;
          return (
            <button
              key={it.url}
              type="button"
              onClick={() => openRow(it.url)}
              className="w-full text-left flex items-center gap-3 rounded-xl border border-gray-200 p-2.5 hover:border-gray-300 transition-colors group"
            >
              {it.thumbnail_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <Photo src={it.thumbnail_url} alt="" aria-hidden frame="w-12 h-12 rounded-lg shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 text-gray-400" />
                </div>
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-0.5">
                  <span className="uppercase tracking-wide">{it.source}</span>
                </div>
                <div className="text-xs text-gray-600 truncate">
                  {[
                    it.room_count != null ? `${it.room_count} ${t("preview.rooms")}` : null,
                    it.area_m2 != null ? `${Math.round(it.area_m2)} m²` : null,
                    it.floor != null && it.total_floors != null
                      ? `${it.floor}/${it.total_floors}`
                      : null,
                    it.complex_name || it.location_name || null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
                {/* Match evidence chips: green = attributes/photos agree,
                    amber = a genuine mismatch or a photo-only re-post. */}
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  {row.kind === "structural" ? (
                    <>
                      {row.d.photos_match && (
                        <span className="inline-flex items-center rounded-md bg-emerald-50 px-1.5 py-0.5 text-xs font-semibold text-emerald-700">
                          {t("result.duplicates.photos_match")}
                        </span>
                      )}
                      {diffs.length === 0 ? (
                        <span className="inline-flex items-center rounded-md bg-emerald-50 px-1.5 py-0.5 text-xs font-semibold text-emerald-700">
                          {t("result.duplicates.match.ok")}
                        </span>
                      ) : (
                        diffs.map((label) => (
                          <span
                            key={label}
                            className="inline-flex items-center rounded-md bg-amber-50 px-1.5 py-0.5 text-xs font-semibold text-amber-700"
                          >
                            {label}
                          </span>
                        ))
                      )}
                    </>
                  ) : (
                    <>
                      <span className="inline-flex items-center rounded-md bg-amber-50 px-1.5 py-0.5 text-xs font-semibold text-amber-700">
                        {t("result.photodups.shared")
                          .replace("{n}", String(row.m.shared_photos))
                          .replace("{m}", String(Math.max(photoQueryTotal, row.m.shared_photos)))}
                      </span>
                      {row.m.different_seller && (
                        <span className="inline-flex items-center rounded-md bg-rose-50 px-1.5 py-0.5 text-xs font-semibold text-rose-700">
                          {t("result.duplicates.other_seller")}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="text-right shrink-0">
                {it.asking_price != null && (
                  <div className="text-sm font-extrabold text-gray-900 tabular-nums whitespace-nowrap">
                    {formatPrice(it.asking_price)}{" "}
                    <span className="text-xs font-medium text-gray-400">{priceUnit}</span>
                  </div>
                )}
                {isCheapest && (
                  <div className="text-xs font-semibold text-emerald-600">
                    {t("result.duplicates.cheapest")}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Live listing drawer for the clicked duplicate - content in
          place, not a jump to the source site (its link lives inside
          the panel). */}
      {active && (
        <ListingDetailPanel
          externalId={active.externalId}
          listingId={active.listingId}
          sourceUrl={active.sourceUrl}
          onClose={() => setActive(null)}
          variant="overlay"
        />
      )}
    </div>
  );
}
